import { useTranslation } from "react-i18next";
import { ReactNode, useContext, useEffect, useMemo } from "react";
import { useRouter } from "@/shared/lib/router/router";
import SideBar from "../sideBar";
import { useToast } from "@/shared/ui/toast/ToastContext";
import { useSidebar } from "@/shared/model/SidebarContext";
import { registerToast } from "@/shared/api/client";
import { authContext } from "@/shared/model/authContext";
import { PERMISSIONS } from "@/shared/config/permissions";
import { canAccessPlatformReports } from "@/entities/report/model/access";
import TitleBar from "./TitleBar";
import { SupportSessionBanner } from "./SupportSessionBanner";
import MobileTopBar from "../../../../shared/ui/MobileTopBar";
import { getMobilePageTitleKey } from "./mobilePageTitle";
import { SidebarProvider } from "@/shared/ui/kit/sidebar";
import { isAppBuildsEnabled } from "@/entities/app-build/lib/appBuildEnvironment";

/** Mesmas larguras de antes: TicketsInbox e crm/chat usam isSidebarCollapsed para recuos. */
const GLOBAL_SIDEBAR_WIDTHS = { "--sidebar-width": "260px", "--sidebar-width-icon": "80px" };


interface LayoutProps {
    children: ReactNode;
    flush?: boolean;
}

export default function Layout({ children, flush }: LayoutProps) {
    const { t } = useTranslation("common");
    const router = useRouter();
    const { showToast } = useToast();
    const { isSidebarCollapsed, setIsSidebarCollapsed } = useSidebar();
    const { isAuthenticated, loading: authLoading, checkPermission, userRoles, isPlatformAdmin } = useContext(authContext);
    const authRoutes = ["/login", "/submit", "/submit/basic", "/submit/client", "/submit/collaborator", "/signup", "/auth/otp", "/auth/reset-password", "/convite/[token]", "/suporte", "/404"];
    const isAuthPage = authRoutes.includes(router.pathname);
    // Dentro de uma obra ou estoque usamos um workspace dedicado: a sidebar global dá lugar
    const isWorkspaceFlow = router.pathname.startsWith("/project/") || router.pathname.startsWith("/storage/");
    useEffect(() => {
        registerToast(showToast);
    }, [showToast]);

    useEffect(() => {
        if (!authLoading && !isAuthPage && !isAuthenticated) {
            router.replace("/login");
        }
    }, [authLoading, isAuthPage, isAuthenticated, router]);

    const hasFinancialPermission =
        checkPermission(PERMISSIONS.CAN_LIST_COSTS) ||
        checkPermission(PERMISSIONS.CAN_LIST_OFFER_COSTS) ||
        checkPermission(PERMISSIONS.CAN_LIST_RECEIVEMENTS) ||
        checkPermission(PERMISSIONS.CAN_LIST_RECEIVEMENTS_GLOBAL);

    const canAccessInventoryModule =
        checkPermission(PERMISSIONS.CAN_LIST_STORAGES) ||
        checkPermission(PERMISSIONS.CAN_LIST_CATEGORIES) ||
        checkPermission(PERMISSIONS.CAN_LIST_OFFERS) ||
        checkPermission(PERMISSIONS.CAN_LIST_TOOLS) ||
        checkPermission(PERMISSIONS.CAN_LIST_STOCK_TOOLS) ||
        checkPermission(PERMISSIONS.CAN_LIST_STOCK_BALANCES) ||
        checkPermission(PERMISSIONS.CAN_LIST_STOCKS) ||
        checkPermission(PERMISSIONS.CAN_LIST_ACTION_HISTORY) ||
        checkPermission(PERMISSIONS.CAN_LIST_SOLICITATIONS);

    const canAccessCurrentRoute = useMemo(() => {
        if (isAuthPage) {
            return true;
        }

        const isAdmin = userRoles?.some(r => r.toUpperCase().includes("ADMIN")) || checkPermission(PERMISSIONS.CAN_LIST_SYSTEM_PROFILES) || checkPermission(PERMISSIONS.CAN_LIST_USERS);

        if (!isAuthenticated) {
            return false;
        }

        switch (router.pathname) {
            case "/":
                return (
                    hasFinancialPermission ||
                    checkPermission(PERMISSIONS.CAN_LIST_PROJECTS) ||
                    checkPermission(PERMISSIONS.CAN_LIST_PARTICIPATING_PROJECTS) ||
                    checkPermission(PERMISSIONS.CAN_LIST_TASKS)
                );
            case "/users":
                return (
                    checkPermission(PERMISSIONS.CAN_LIST_USERS) ||
                    checkPermission(PERMISSIONS.CAN_LIST_SYSTEM_PROFILES) ||
                    checkPermission(PERMISSIONS.CAN_LIST_SUPPLIERS) ||
                    checkPermission(PERMISSIONS.CAN_LIST_COLLABORATORS)
                );
            case "/projects":
            case "/project/[projectId]":
            case "/project/[projectId]/stock":
                return checkPermission(PERMISSIONS.CAN_LIST_PROJECTS) || checkPermission(PERMISSIONS.CAN_LIST_PARTICIPATING_PROJECTS);
            case "/project/[projectId]/gantt":
                return checkPermission(PERMISSIONS.CAN_LIST_STEPS);
            case "/financial":
                return (
                    hasFinancialPermission ||
                    checkPermission(PERMISSIONS.CAN_LIST_PURCHASE_ORDERS) ||
                    checkPermission(PERMISSIONS.CAN_LIST_PURCHASE_ORDER_LISTS) ||
                    checkPermission(PERMISSIONS.CAN_LIST_SOLICITATIONS)
                );
            case "/settings":
                return (
                    checkPermission(PERMISSIONS.CAN_LIST_SYSTEM_PROFILES) ||
                    checkPermission(PERMISSIONS.CAN_LIST_ROLES) ||
                    checkPermission(PERMISSIONS.CAN_LIST_PERMISSION) ||
                    checkPermission(PERMISSIONS.CAN_LIST_STEP_NAMES) ||
                    checkPermission(PERMISSIONS.CAN_LIST_TASK_NAMES)
                );
            case "/crm/chat":
                return checkPermission(PERMISSIONS.CAN_ACCESS_CRM) || isAdmin;
            case "/crm/contacts":
                return (
                    checkPermission(PERMISSIONS.CAN_LIST_CONTACTS) ||
                    checkPermission(PERMISSIONS.CAN_LIST_SUBMITS) ||
                    checkPermission(PERMISSIONS.CAN_APPROVE_SUBMIT)
                );
            case "/admin/submits":
                return (
                    checkPermission(PERMISSIONS.CAN_LIST_SUBMITS) ||
                    checkPermission(PERMISSIONS.CAN_APPROVE_SUBMIT)
                );
            // Centro de entregas e erros são da plataforma (com o console ligado, o main.tsx já os mandou para lá).
            case "/admin/entregas/[[...slug]]":
                return isPlatformAdmin;
            case "/admin/app-builds":
                return isPlatformAdmin && isAppBuildsEnabled();
            case "/admin/app-device/[sessionId]":
                return isPlatformAdmin && isAppBuildsEnabled();
            case "/admin/errors":
                return isPlatformAdmin;
            case "/platform/tenants":
            case "/platform/tenants/new":
            case "/platform/tenants/[id]":
            case "/platform/storage":
                return isPlatformAdmin;
            case "/platform/reports":
            case "/platform/reports/templates":
            case "/platform/reports/templates/[id]":
            case "/platform/reports/playground":
                return canAccessPlatformReports(isPlatformAdmin);
            case "/storages":
            case "/storage/[storageId]":
            case "/inventory":
                return canAccessInventoryModule;
            default:
                return true;
        }
    }, [
        isAuthPage,
        isAuthenticated,
        router.pathname,
        hasFinancialPermission,
        canAccessInventoryModule,
        checkPermission,
        isPlatformAdmin,
    ]);

    if (authLoading) {
        return (
            <div className="h-dvh w-full flex flex-col bg-light_gray dark:bg-night overflow-hidden text-zinc-900 dark:text-zinc-300">
                <TitleBar />
                <div className="flex-1 flex items-center justify-center">
                    <div className="w-10 h-10 border-4 border-dark_green border-t-transparent rounded-full animate-spin" />
                </div>
            </div>
        );
    }

    // Auth pages should be full screen without the sidebar or standard main paddings
    if (isAuthPage) {
        return (
            <div className="h-dvh w-full flex flex-col overflow-hidden bg-white dark:bg-night">
                <TitleBar />
                <div className="flex-1 min-h-0 overflow-auto">
                    {children}
                </div>
            </div>
        );
    }

    const showGlobalNav = !flush && isAuthenticated && !isWorkspaceFlow;

    const content = (
        <div className="flex-1 flex flex-col min-w-0 min-h-0 overflow-hidden">
            {showGlobalNav && (
                <MobileTopBar
                    title={t(getMobilePageTitleKey(router.pathname))}
                    menuLabel={t("mobileHeader.openMenu")}
                />
            )}
            <main id="main-content" className={`flex-1 min-h-0 w-full overflow-auto ${isWorkspaceFlow ? 'flex flex-col' : `custom-scrollbar ${flush || router.pathname === '/editor' ? '' : 'px-4 md:px-8 pt-4 md:pt-8 pb-8'}`}`}>
                {isAuthenticated ? (
                    canAccessCurrentRoute ? (
                        children
                    ) : (
                        <div className="w-full rounded-sm border border-light_gray dark:border-zinc-700 bg-white dark:bg-night_surface p-10 text-center shadow-card">
                            <h2 className="text-dark_green text-2xl font-bold">{t("general.noPermissionPage")}</h2>
                            <p className="mt-2 text-dark_gray dark:text-zinc-300">{t("general.noPermissionHint")}</p>
                        </div>
                    )
                ) : null}
            </main>
        </div>
    );

    return (
        <div className="h-dvh flex flex-col overflow-hidden bg-light_gray dark:bg-night text-zinc-900 dark:text-zinc-300 font-sans">
            <a
                href="#main-content"
                className="sr-only focus:not-sr-only focus:absolute focus:z-fab focus:p-4 focus:bg-white focus:text-dark_green focus:shadow-lg focus:left-4 focus:top-4 focus:rounded-md font-bold"
            >
                Saltar para o conteúdo principal
            </a>
            <TitleBar />
            <SupportSessionBanner />

            {showGlobalNav ? (
                <SidebarProvider
                    open={!isSidebarCollapsed}
                    onOpenChange={(open) => setIsSidebarCollapsed(!open)}
                    style={GLOBAL_SIDEBAR_WIDTHS}
                    className="flex-1 min-h-0 overflow-hidden"
                >
                    <SideBar />
                    {content}
                </SidebarProvider>
            ) : (
                <div className="flex-1 flex min-h-0 overflow-hidden relative">{content}</div>
            )}
        </div>
    );
}
